import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { WORLD_REGIONS } from "../../utils/regions";

// Palette + type scale lifted 1:1 from AstriaDesign3.html's :root and
// .ww / .ww-badge / .ww-head / .ww-chip rules.
const COLORS = {
  amethyst: "#A78BFF",
  darkAmethyst: "#F7F3FA",
  cream: "#221A29",
  muted: "#C8BFD1",
  hairline: "rgba(255,255,255,0.08)",
  ink: "#C8BFD1",
};

const FONTS = {
  body: '"Manrope", system-ui, -apple-system, sans-serif',
};

const WorldwideSection: React.FC = () => {
  const { t } = useTranslation();
  return (
    <section className="w-full" style={{ marginTop: "2.5rem" }}>
      <div
        className="relative overflow-hidden bg-[#1A131F]"
        style={{
          maxWidth: 900,
          margin: "0 auto",
          borderRadius: 32,
          border: `1px solid ${COLORS.hairline}`,
          padding: "2.5rem 1.5rem 1.75rem",
          boxShadow: "0 8px 25px rgba(0,0,0,0.06)",
          fontFamily: FONTS.body,
        }}
      >
        {/* Warm wash in the corners — keeps the panel from reading as a plain box */}
        <div
          className="pointer-events-none absolute rounded-full"
          style={{
            top: "-7rem",
            right: "-6rem",
            width: 420,
            height: 420,
            opacity: 0.3,
            filter: "blur(30px)",
            background:
              "radial-gradient(circle, rgba(229,184,173,0.34) 0%, rgba(229,184,173,0) 70%)",
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute rounded-full"
          style={{
            bottom: "-8rem",
            left: "-6rem",
            width: 380,
            height: 380,
            opacity: 0.25,
            filter: "blur(34px)",
            background:
              "radial-gradient(circle, rgba(109,76,146,0.28) 0%, rgba(109,76,146,0) 70%)",
          }}
          aria-hidden="true"
        />

        {/* Header */}
        <div
          className="relative text-center mx-auto"
          style={{ maxWidth: 620 }}
        >
          <div
            className="inline-flex items-center rounded-full uppercase"
            style={{
              gap: "0.5rem",
              padding: "0.375rem 0.875rem",
              fontSize: "0.625rem",
              fontWeight: 600,
              letterSpacing: "0.22em",
              backgroundColor: COLORS.cream,
              color: COLORS.amethyst,
            }}
          >
            {t("astriaHome.worldwide.badge")}
          </div>

          <h2
            style={{
              marginTop: "1rem",
              fontSize: "1.75rem",
              fontWeight: 700,
              lineHeight: 1.25,
              color: COLORS.darkAmethyst,
            }}
          >
            {t("astriaHome.worldwide.title")}
          </h2>

          <p
            style={{
              marginTop: "0.75rem",
              fontSize: "0.9rem",
              lineHeight: 1.7,
              color: COLORS.muted,
            }}
          >
            {t("astriaHome.worldwide.subtitle")}
          </p>
        </div>

        {/* Region chips */}
        <div
          className="relative flex flex-wrap justify-center"
          style={{ marginTop: "1.5rem", gap: "0.6rem" }}
        >
          {WORLD_REGIONS.map((region, i) => (
            <motion.button
              key={region.value}
              type="button"
              initial={{ opacity: 0, y: 8 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{
                duration: 0.35,
                // Capped so the last chips don't visibly lag behind the first.
                delay: Math.min(i * 0.025, 0.3),
                ease: [0.22, 1, 0.36, 1],
              }}
              className="inline-flex items-center rounded-full bg-[#1A131F]"
              style={{
                gap: "0.5rem",
                padding: "0.5rem 0.95rem",
                fontSize: "0.82rem",
                fontWeight: 600,
                color: COLORS.ink,
                border: `1px solid ${COLORS.hairline}`,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-1px)";
                e.currentTarget.style.borderColor = "rgba(109,76,146,0.35)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.borderColor = COLORS.hairline;
              }}
            >
              {t(`astriaHome.regionName.${region.value}`, region.label)}
            </motion.button>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WorldwideSection;
